import type { ReactNode } from "react";
import classNames from "classnames";
import { Button } from "../Button/Button";

type AlertType = "info" | "success" | "warning" | "error";

const ALERT_CTA_BUTTON_TYPE: Record<
  AlertType,
  "primary" | "secondary" | "accent" | "error" | "base"
> = {
  info: "primary",
  success: "accent",
  warning: "secondary",
  error: "error",
};

const ALERT_CLASSES: Record<AlertType, string> = {
  info: "border-info-200 bg-info-50/70 text-info-700 dark:border-info-700/40 dark:bg-info-500/10 dark:text-info-300",
  success:
    "border-success-200 bg-success-50/70 text-success-700 dark:border-success-700/40 dark:bg-success-500/10 dark:text-success-300",
  warning:
    "border-warning-200 bg-warning-50/70 text-warning-700 dark:border-warning-700/40 dark:bg-warning-500/10 dark:text-warning-300",
  error:
    "border-error-200 bg-error-50/70 text-error-700 dark:border-error-700/40 dark:bg-error-500/10 dark:text-error-300",
};

const ALERT_ICON_CLASSES: Record<AlertType, string> = {
  info: "text-info-600 dark:text-info-300",
  success: "text-success-600 dark:text-success-300",
  warning: "text-warning-600 dark:text-warning-300",
  error: "text-error-600 dark:text-error-300",
};

interface AlertProps {
  icon?: string;
  title?: ReactNode;
  message?: ReactNode;
  ctaText?: ReactNode;
  cta?: () => void;
  type?: AlertType;
}

export const Alert = ({
  icon,
  title,
  message,
  ctaText = undefined,
  cta = undefined,
  type = "info",
}: AlertProps) => {
  return (
    <div
      className={classNames(
        "rounded-lg border px-4 py-3",
        ALERT_CLASSES[type]
      )}
    >
      <div className={classNames("grid-cols-24 grid h-auto gap-4 rounded-xl border-2 border-solid px-3", ALERT_CLASSES[type])}>
        <div className="hidden items-center justify-center lg:col-span-1 lg:flex">
          <div className={classNames(icon, "h-10 w-10", ALERT_ICON_CLASSES[type])} />
        </div>
        <div className="col-span-15 lg:col-span-19 flex flex-col items-start justify-start py-2">
          <div className="font-header text-sm font-semibold xl:text-lg">
            {title}
          </div>
          <div className="xl:text-md font-sans text-xs font-medium">
            {message}
          </div>
        </div>
        {cta && (
          <div className="col-span-6 flex items-center justify-center lg:col-span-4">
            <Button
              type={ALERT_CTA_BUTTON_TYPE[type]}
              className={`w-full text-center`}
              onClick={() => cta()}
            >
              {ctaText}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
